import React from 'react';
import { Cuboid, Video, Music, Image as ImageIcon, Type } from 'lucide-react';
import {
  Y_SPACING,
  X_SPACING,
  MIN_EVENT_SPACING,
  NODE_WIDTH,
  NODE_HEIGHT,
} from './VrMindMapConstants';

export const getAssetType = (asset) => {
  if (asset.mesh_code) return 'Mesh';
  if (asset.video_code) return 'Video';
  if (asset.audio_code) return 'Audio';
  if (asset.image_code) return 'Image';
  if (asset.text_code) return 'Text';
  return 'Unknown';
};

export const getAssetIcon = (type) => {
  switch (type) {
    case 'Mesh':
      return <Cuboid className="w-4 h-4" />;
    case 'Video':
      return <Video className="w-4 h-4" />;
    case 'Audio':
      return <Music className="w-4 h-4" />;
    case 'Image':
      return <ImageIcon className="w-4 h-4" />;
    case 'Text':
      return <Type className="w-4 h-4" />;
    default:
      return <Cuboid className="w-4 h-4" />;
  }
};

export const getStorageKey = (assetId) => {
  return `vr-mindmap-positions-${assetId}`;
};

export const loadNodePositions = (assetId) => {
  if (!assetId) return {};
  try {
    const stored = localStorage.getItem(getStorageKey(assetId));
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error loading node positions:', error);
    return {};
  }
};

export const saveNodePositions = (assetId, positions) => {
  if (!assetId) return;
  try {
    localStorage.setItem(getStorageKey(assetId), JSON.stringify(positions));
  } catch (error) {
    console.error('Error saving node positions:', error);
  }
};

export const convertToFlowGraph = (
  assetData,
  selectedAsset,
  handleEditNode,
  openAddEventModal,
  openAddActionModal,
  handleDeleteEvent,
  startX = 400,
  startY = 100
) => {
  if (!assetData || !selectedAsset) return { nodes: [], edges: [] };

  const assetId = selectedAsset.id || selectedAsset._id;
  const savedPositions = loadNodePositions(assetId);

  const flowNodes = [];
  const flowEdges = [];
  let nodeIdCounter = 0;
  let yOffset = startY;
  const nodeIdToStableKey = new Map();

  // Track used positions to prevent overlaps
  const usedPositions = new Set();

  // Helper to check and adjust position to avoid overlaps
  const getNonOverlappingPosition = (
    x,
    y,
    width = NODE_WIDTH,
    height = NODE_HEIGHT
  ) => {
    const key = (pos) => `${Math.round(pos.x / 10)}_${Math.round(pos.y / 10)}`;
    let adjustedX = x;
    let adjustedY = y;
    let attempts = 0;
    const maxAttempts = 100;

    while (attempts < maxAttempts) {
      const posKey = key({ x: adjustedX, y: adjustedY });
      if (!usedPositions.has(posKey)) {
        // Check if the area is clear
        let isClear = true;
        for (
          let checkX = adjustedX - width / 2;
          checkX <= adjustedX + width / 2;
          checkX += width / 4
        ) {
          for (
            let checkY = adjustedY - height / 2;
            checkY <= adjustedY + height / 2;
            checkY += height / 4
          ) {
            if (usedPositions.has(key({ x: checkX, y: checkY }))) {
              isClear = false;
              break;
            }
          }
          if (!isClear) break;
        }

        if (isClear) {
          // Mark the area as used
          for (
            let markX = adjustedX - width / 2;
            markX <= adjustedX + width / 2;
            markX += width / 4
          ) {
            for (
              let markY = adjustedY - height / 2;
              markY <= adjustedY + height / 2;
              markY += height / 4
            ) {
              usedPositions.add(key({ x: markX, y: markY }));
            }
          }
          return { x: adjustedX, y: adjustedY };
        }
      }

      // Try next position horizontally
      adjustedX += X_SPACING;
      attempts++;
    }

    // Fallback: try moving down
    adjustedY += Y_SPACING;
    adjustedX = x;
    attempts = 0;
    while (attempts < maxAttempts) {
      const posKey = key({ x: adjustedX, y: adjustedY });
      if (!usedPositions.has(posKey)) {
        usedPositions.add(posKey);
        return { x: adjustedX, y: adjustedY };
      }
      adjustedX += X_SPACING;
      attempts++;
    }

    // Last resort: return original position
    return { x, y };
  };

  const createNodeId = (stableKey) => {
    const id = `node-${nodeIdCounter++}`;
    if (stableKey) {
      nodeIdToStableKey.set(id, stableKey);
    }
    return id;
  };

  // Root node
  const rootKey = 'root';
  const rootId = createNodeId(rootKey);
  const savedRootPos = savedPositions[rootKey];
  const rootPosition =
    savedRootPos ||
    getNonOverlappingPosition(
      startX,
      yOffset,
      NODE_WIDTH * 1.5,
      NODE_HEIGHT * 1.5
    );
  flowNodes.push({
    id: rootId,
    type: 'root',
    position: rootPosition,
    data: { label: assetData.name, stableKey: rootKey },
  });

  yOffset += Y_SPACING;

  // Helper function to process a property node
  const processProperty = (prop, parentId, parentKey, x, y, level = 0) => {
    const propKey = `${parentKey}-${prop.name.toLowerCase().replace(/\s+/g, '-')}`;
    const propId = createNodeId(propKey);
    const savedPos = savedPositions[propKey];
    const propPosition = savedPos || getNonOverlappingPosition(x, y);

    flowNodes.push({
      id: propId,
      type: 'property',
      position: propPosition,
      data: {
        label: prop.name,
        value: prop.value === undefined ? 'N/A' : String(prop.value),
        type: prop.type,
        path: prop.path,
        apiPath: prop.apiPath,
        eventId: prop.eventId,
        options: prop.options,
        min: prop.min,
        max: prop.max,
        editable: true,
        originalData: prop,
        onEdit: handleEditNode,
        stableKey: propKey,
      },
    });

    flowEdges.push({
      id: `edge-${parentId}-${propId}`,
      source: parentId,
      target: propId,
      type: 'sConnector',
      animated: false,
    });

    const propertyResult = {
      propId,
      propKey,
      x: propPosition.x + X_SPACING,
      y: propPosition.y,
    };

    if (prop.children && prop.children.length > 0) {
      let childX = propPosition.x;
      let childY = propPosition.y + Y_SPACING;
      let maxChildX = propertyResult.x;

      for (const childSection of prop.children) {
        if (!childSection) continue;
        const result = processSubCategory(
          childSection,
          propId,
          propKey,
          childX,
          childY,
          level + 1
        );
        childX = Math.max(childX, result.x);
        childY = Math.max(childY, result.y);
        maxChildX = Math.max(maxChildX, result.x);
      }

      propertyResult.x = maxChildX;
    }

    return propertyResult;
  };

  // Helper function to process subcategories and properties recursively
  const processSubCategory = (
    subCategory,
    parentId,
    parentKey,
    x,
    y,
    level = 0
  ) => {
    const subKey = `${parentKey}-${subCategory.name.toLowerCase().replace(/\s+/g, '-')}`;
    const subId = createNodeId(subKey);
    const savedPos = savedPositions[subKey];
    const subPosition = savedPos || getNonOverlappingPosition(x, y);

    const isEvent = !!subCategory.eventData;

    flowNodes.push({
      id: subId,
      type: 'subCategory',
      position: subPosition,
      data: {
        label: subCategory.name,
        originalData: subCategory,
        stableKey: subKey,
        onEdit: isEvent
          ? () => {
              if (subCategory.eventData && handleEditNode) {
                handleEditNode({
                  originalData: { eventData: subCategory.eventData },
                });
              }
            }
          : undefined,
        onDelete: isEvent
          ? () => {
              if (subCategory.eventData?._id && handleDeleteEvent) {
                handleDeleteEvent(subCategory.eventData);
              }
            }
          : undefined,
      },
    });

    flowEdges.push({
      id: `edge-${parentId}-${subId}`,
      source: parentId,
      target: subId,
      type: 'sConnector',
      animated: false,
    });

    let currentX = subPosition.x;
    let currentY = subPosition.y + Y_SPACING;
    let maxX = currentX;

    // Process children (properties or nested subcategories)
    if (subCategory.children && subCategory.children.length > 0) {
      // Special handling for events - show trigger and action flow vertically
      if (isEvent) {
        const filteredProps = subCategory.children.filter(
          (prop) =>
            prop.name !== 'URL' &&
            prop.name !== 'Affected Asset' &&
            prop.name !== 'Asset Model' &&
            prop.name !== 'Value Threshold' &&
            prop.name !== 'Manipulation Trigger'
        );

        const vrTriggerProp = filteredProps.find(
          (prop) => prop.name === 'VR Trigger'
        );
        const actionProp = filteredProps.find((prop) => prop.name === 'Action');

        if (vrTriggerProp) {
          const triggerKey = `${subKey}-trigger`;
          const triggerId = createNodeId(triggerKey);
          const savedTriggerPos = savedPositions[triggerKey];
          const triggerPosition =
            savedTriggerPos ||
            getNonOverlappingPosition(subPosition.x, currentY);
          const hasAction = !!actionProp;

          flowNodes.push({
            id: triggerId,
            type: 'property',
            position: triggerPosition,
            data: {
              label: vrTriggerProp.name,
              value: vrTriggerProp.value,
              type: vrTriggerProp.type,
              path: vrTriggerProp.path,
              apiPath: vrTriggerProp.apiPath,
              eventId: vrTriggerProp.eventId,
              options: vrTriggerProp.options,
              min: vrTriggerProp.min,
              max: vrTriggerProp.max,
              editable: true,
              originalData: vrTriggerProp,
              onEdit: handleEditNode,
              onTriggerClick: (data) => {
                if (data.eventId) {
                  openAddActionModal(data.eventId, data.value);
                }
              },
              hasAction: hasAction,
              stableKey: triggerKey,
            },
          });

          flowEdges.push({
            id: `edge-${subId}-${triggerId}`,
            source: subId,
            target: triggerId,
            type: 'sConnector',
            animated: false,
          });

          if (actionProp) {
            const actionKey = `${subKey}-action`;
            const actionId = createNodeId(actionKey);
            const savedActionPos = savedPositions[actionKey];
            const actionPosition =
              savedActionPos ||
              getNonOverlappingPosition(
                triggerPosition.x,
                triggerPosition.y + Y_SPACING
              );
            flowNodes.push({
              id: actionId,
              type: 'property',
              position: actionPosition,
              data: {
                label: actionProp.name,
                value: actionProp.value,
                type: actionProp.type,
                path: actionProp.path,
                apiPath: actionProp.apiPath,
                eventId: actionProp.eventId,
                options: actionProp.options,
                min: actionProp.min,
                max: actionProp.max,
                editable: true,
                originalData: actionProp,
                onEdit: handleEditNode,
                stableKey: actionKey,
              },
            });

            flowEdges.push({
              id: `edge-${triggerId}-${actionId}`,
              source: triggerId,
              target: actionId,
              type: 'sConnector',
              animated: true,
            });
          }
        }
      } else {
        // Regular processing for non-event subcategories - arrange horizontally
        for (const child of subCategory.children) {
          if (child.children) {
            // It's a nested subcategory - place it horizontally
            const result = processSubCategory(
              child,
              subId,
              subKey,
              currentX,
              currentY,
              level + 1
            );
            currentX = result.x;
            maxX = Math.max(maxX, currentX);
          } else {
            // It's a property - place it horizontally
            const result = processProperty(
              child,
              subId,
              subKey,
              currentX,
              currentY,
              level
            );
            currentX = result.x;
            maxX = Math.max(maxX, currentX);
          }
        }
      }
    }

    return { subId, subKey, x: maxX, y: currentY };
  };

  // Process all categories
  if (assetData.children) {
    // Separate events container from other categories
    const eventsContainer = assetData.children.find(
      (category) => category.isEventsContainer
    );
    const otherCategories = assetData.children.filter(
      (category) => !category.isEventsContainer
    );

    // Process VR Manipulation category - arrange subcategories horizontally
    let categoryX = startX - 600;
    for (const category of otherCategories) {
      const categoryKey = category.name.toLowerCase().replace(/\s+/g, '-');
      const categoryId = createNodeId(categoryKey);
      const savedCategoryPos = savedPositions[categoryKey];
      const categoryPosition =
        savedCategoryPos || getNonOverlappingPosition(categoryX, yOffset);

      flowNodes.push({
        id: categoryId,
        type: 'category',
        position: categoryPosition,
        data: {
          label: category.name,
          stableKey: categoryKey,
        },
      });

      flowEdges.push({
        id: `edge-${rootId}-${categoryId}`,
        source: rootId,
        target: categoryId,
        type: 'sConnector',
        animated: false,
      });

      let currentX = categoryPosition.x;
      let currentY = categoryPosition.y + Y_SPACING;
      let maxX = currentX;

      // Process subcategories horizontally
      if (category.children && category.children.length > 0) {
        for (const subCategory of category.children) {
          const result = processSubCategory(
            subCategory,
            categoryId,
            categoryKey,
            currentX,
            currentY
          );
          currentX = result.x;
          maxX = Math.max(maxX, currentX);
        }
      }

      // Move to next category position (further right)
      categoryX = maxX + X_SPACING * 2;
    }

    // Process events container separately (with special layout)
    if (eventsContainer) {
      const containerKey = 'events-container';
      const categoryId = createNodeId(containerKey);
      const containerPosition = savedPositions[containerKey] || {
        x: startX,
        y: yOffset,
      };
      flowNodes.push({
        id: categoryId,
        type: 'eventContainer',
        position: containerPosition,
        data: {
          label: eventsContainer.name,
          originalData: eventsContainer,
          onAdd: () => openAddEventModal(),
          stableKey: containerKey,
        },
      });

      flowEdges.push({
        id: `edge-${rootId}-${categoryId}`,
        source: rootId,
        target: categoryId,
        type: 'sConnector',
        animated: false,
      });

      // Process events
      if (eventsContainer.children && eventsContainer.children.length > 0) {
        let subY = containerPosition.y + Y_SPACING;
        const eventItems = eventsContainer.children;

        const eventPositions = [];
        let eventStartX =
          startX - ((eventItems.length - 1) * MIN_EVENT_SPACING) / 2;

        for (let i = 0; i < eventItems.length; i++) {
          const subItem = eventItems[i];
          const eventKey = `event-${i}`;
          const savedPos = savedPositions[eventKey];

          let eventX;
          let eventY = subY;

          if (savedPos) {
            eventX = savedPos.x;
            eventY = savedPos.y;

            for (let j = 0; j < i; j++) {
              const prevPos = eventPositions[j];
              const distance = Math.sqrt(
                Math.pow(eventX - prevPos.x, 2) +
                  Math.pow(eventY - prevPos.y, 2)
              );
              if (distance < MIN_EVENT_SPACING) {
                eventX = prevPos.x + MIN_EVENT_SPACING;
                break;
              }
            }
          } else {
            eventX = eventStartX;
          }

          eventPositions.push({ x: eventX, y: eventY });
          processSubCategory(subItem, categoryId, containerKey, eventX, eventY);
          eventStartX += MIN_EVENT_SPACING;
        }
      }
    }
  }

  return { nodes: flowNodes, edges: flowEdges, nodeIdToStableKey };
};
